import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { getColor } from '../../styling/mixins/colors';
import Icon from '../Icon';
import Input from '../Inputs/Input';
import { debounce } from 'lodash-es';

interface QuantityInputAttributes {
  className?: string;
  wrapperClassName?: string;
  onChange?: (quantity: number) => void;
  quantity?: number;
  showButtons?: boolean;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}

const QuantityInput = (props: QuantityInputAttributes) => {
  const {
    className = '',
    wrapperClassName = '',
    onChange,
    quantity,
    showButtons = false,
    min = 1,
    max,
    step = 1,
    disabled = false,
  } = props;

  const [internalValue, setInternalValue] = useState<number>(quantity ?? min);

  const debounceOnChange = useCallback(
    debounce((value: number) => {
      if (onChange) {
        onChange(value);
      }
    }, 600),
    [onChange]
  );

  const onInputChanged = (value, immediate = false) => {
    let parsedValue: number | undefined = parseFloat(value);
    if (!parsedValue) {
      parsedValue = 0;
    }
    if (parsedValue <= 0) return;

    if (max !== undefined && parsedValue > max) {
      parsedValue = max;
    }

    setInternalValue(parsedValue);

    if (immediate && onChange) {
      // Immediate update for button clicks otherwise it will cause a delay in updating the quantity
      // If the user clicks buy button before the quantity is updated, the quantity in the request payload will be just 1.
      onChange(parsedValue);
    } else {
      // Debounced update for just manual input
      debounceOnChange(parsedValue);
    }
  };

  useEffect(() => {
    // Cleanup debounce when the component unmounts
    return () => {
      debounceOnChange.cancel();
    };
  }, [debounceOnChange]);

  return (
    <StyledQuantityInput
      className={`quantity-input ${wrapperClassName}`.trim()}
    >
      <Input
        className={className}
        type="number"
        value={internalValue ?? ''}
        onChange={(e) => onInputChanged(e.target.value)}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
      />
      {showButtons && (
        <>
          <StyledQuantityInputIcon
            name="minus"
            className="minus-icon"
            role="button"
            onClick={() => onInputChanged(internalValue - step, true)}
            $disabled={internalValue <= min || disabled}
          />
          <StyledQuantityInputIcon
            name="plus"
            className="plus-icon"
            role="button"
            onClick={() => onInputChanged(internalValue + step, true)}
            $disabled={disabled}
          />
        </>
      )}
    </StyledQuantityInput>
  );
};

export default QuantityInput;

interface StyledIconProps {
  $disabled?: boolean;
}

const StyledQuantityInput = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;

  & div {
    flex: 1;
  }

  input {
    text-align: center;
  }
`;

const StyledQuantityInputIcon = styled(Icon) <StyledIconProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  width: 16px;
  height: 16px;
  color: ${getColor('grey', 900)};

  &.minus-icon {
    left: 12px;
    opacity: ${(p) => (p.$disabled ? 0.24 : 1)};
    cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  }

  &.plus-icon {
    right: 12px;
  }
`;